/**
 * Where a ring is kept: one `Communication` per call.
 *
 * `Communication` is what FHIR has for "A sent this to B", which is exactly
 * what a ring is. It is NOT messaging, and this is not the chat we are not
 * building — the resource carries no content and never will; it exists so the
 * other party's client can see that somebody is calling, and so there is a row
 * to audit against afterwards.
 *
 * CATEGORY IS THE DISCRIMINATOR, and it is load-bearing rather than
 * decorative. Two other aggregates live on this resource type: the
 * OutboundMessage notification outbox (already carved out of the clinician
 * access policy by category) and, one day, a chat. Three aggregates over one
 * resource type only works if every reader filters, so this one refuses to read
 * anything that is not a colleague call rather than trusting the id it was
 * handed. A call id is a client-supplied string; without the check it is a way
 * to drive an outbox row through a state machine built for something else.
 *
 * THERE IS NO `subject`, deliberately. A colleague call has no patient, and a
 * subject reference would file the row — and every AuditEvent that names it —
 * in somebody's chart on the strength of a guess.
 *
 * NO DOMAIN ENTITY, also deliberately. A call is ephemeral infrastructure:
 * nothing derives state from it, nothing reads it a minute later, and it has no
 * lifecycle anyone outside this feature observes. An entity, a mapper and an
 * identity system would be three pieces of ceremony guarding a record whose
 * whole life is sixty seconds.
 */
import type { Communication } from "@medplum/fhirtypes";
import { asCallId, type Call, type CallId, type CallRegistry, type CallStatus } from "@repo/conferencing";
import { COLLEAGUE_CALL_CATEGORY_SYSTEM } from "@repo/fhir-systems/code-systems";

/** The one code under the colleague-call system. */
export const COLLEAGUE_CALL_CATEGORY_CODE = "colleague-call";

/**
 * The four states, in FHIR's EventStatus vocabulary.
 *
 * `answered` is `completed` because the resource is the RING, not the
 * conversation: the ring's job is done the moment it is picked up, and a call
 * that then runs for half an hour leaves this row alone. `declined` and
 * `abandoned` are told apart — `stopped` for the party being rung saying no,
 * `not-done` for the party who rang giving up — because they look identical
 * from outside and mean opposite things to the two people involved.
 */
const TO_WIRE = {
  ringing: "in-progress",
  answered: "completed",
  declined: "stopped",
  abandoned: "not-done",
} as const satisfies Record<CallStatus, Communication["status"]>;

const FROM_WIRE: Partial<Record<NonNullable<Communication["status"]>, CallStatus>> = Object.fromEntries(
  Object.entries(TO_WIRE).map(([status, wire]) => [wire, status as CallStatus]),
);

/** The slice of the FHIR adapter a ring needs. Narrow so a fake is three methods. */
export interface CallRegistryFhir {
  create<T extends Communication>(resource: T): Promise<T>;
  read<T extends Communication>(resourceType: string, id: string): Promise<T>;
  update<T extends Communication>(resource: T): Promise<T>;
}

function isColleagueCall(row: Communication): boolean {
  return (
    row.category?.some((c) =>
      c.coding?.some(
        (coding) => coding.system === COLLEAGUE_CALL_CATEGORY_SYSTEM && coding.code === COLLEAGUE_CALL_CATEGORY_CODE,
      ),
    ) ?? false
  );
}

/**
 * Everything a `Call` needs, or nothing. A row missing a party, carrying a
 * status the state machine has no word for, or belonging to another aggregate
 * is not half a call — it is not one, and answering with a partly-guessed one
 * would make it answerable.
 *
 * EXPORTED FOR THE BROWSER, which reads the same rows off a subscription. The
 * alternative was the receiving client keeping its own copy of the status
 * vocabulary and the category check, and a client that read `stopped` as
 * "ringing" would offer an answer button for a call somebody already declined.
 */
export function colleagueCallFromCommunication(row: Communication): Call | undefined {
  if (!row.id || !isColleagueCall(row)) return undefined;
  const from = row.sender?.reference;
  const to = row.recipient?.[0]?.reference;
  const status = row.status ? FROM_WIRE[row.status] : undefined;
  if (!from || !to || !row.sent || !status) return undefined;
  return { id: asCallId(row.id), from, to, ringingSince: new Date(row.sent), status };
}

export function createFhirCallRegistry(fhirAdapter: CallRegistryFhir): CallRegistry {
  /** Read a row and insist it is one of ours, or say so. */
  async function loadRow(id: CallId): Promise<Communication | undefined> {
    let row: Communication;
    try {
      row = await fhirAdapter.read<Communication>("Communication", id);
    } catch {
      // A call that is not there is not an error to this port — the state
      // machine above turns "no such call" into its own refusal.
      return undefined;
    }
    return isColleagueCall(row) ? row : undefined;
  }

  return {
    async place(from, to, ringingSince) {
      const created = await fhirAdapter.create<Communication>({
        resourceType: "Communication",
        status: TO_WIRE.ringing,
        category: [{ coding: [{ system: COLLEAGUE_CALL_CATEGORY_SYSTEM, code: COLLEAGUE_CALL_CATEGORY_CODE }] }],
        sender: { reference: from },
        recipient: [{ reference: to }],
        sent: ringingSince.toISOString(),
      });

      const call = colleagueCallFromCommunication(created);
      if (!call) throw new Error("The server did not return a usable colleague call");
      return call;
    },

    async get(id) {
      const row = await loadRow(id);
      return row ? colleagueCallFromCommunication(row) : undefined;
    },

    async setStatus(id, status) {
      const row = await loadRow(id);
      if (!row) throw new Error(`Communication/${id} is not a colleague call`);

      const saved = await fhirAdapter.update<Communication>({ ...row, status: TO_WIRE[status] });
      const call = colleagueCallFromCommunication(saved);
      if (!call) throw new Error(`Communication/${id} is no longer a usable colleague call`);
      return call;
    },
  };
}
